import {Chess} from 'chess.js';

/**
 * Classroom puzzles store `starting_fen` = the position the coach began from
 * (often the normal start) and `move_list` = the line they played. The puzzle's
 * actual board is `starting_fen` with that line applied — rendering the raw
 * `starting_fen` makes every puzzle look like the standard start ("not loaded").
 *
 * Replays move_list onto the FEN. Entries are `{ply, move}` objects (or plain
 * strings) where `move` is UCI: "e2e4" with an optional promotion char ("e7e8q").
 * Stops at the first illegal/bad move and returns however far it got; falls back
 * to the raw FEN on empty or unparseable input. Never throws.
 */
export function resolvePuzzleFen(
  startingFen?: string | null,
  moveList?: unknown,
): string | undefined {
  const base = startingFen || undefined;
  if (!base || !Array.isArray(moveList) || moveList.length === 0) {
    return base;
  }

  let game: Chess;
  try {
    game = new Chess(base);
  } catch {
    return base;
  }

  for (const entry of moveList) {
    const uci: string | undefined =
      typeof entry === 'string' ? entry : (entry as {move?: string})?.move;
    if (!uci || uci.length < 4) {
      continue;
    }
    try {
      const res = game.move({
        from: uci.slice(0, 2),
        to: uci.slice(2, 4),
        promotion: uci.length > 4 ? uci.slice(4, 5) : undefined,
      });
      if (!res) {
        break;
      }
    } catch {
      break;
    }
  }

  return game.fen();
}
